/**
 * Cmd+K command palette.
 *
 * Single global search across:
 *   - Sidebar navigation items (instant, in-memory)
 *   - Workflows by name (cached fetch)
 *   - Ingress sources by name (cached fetch)
 *   - Canonical models by type (cached fetch)
 *
 * Keyboard:
 *   Cmd/Ctrl + K  → open / toggle
 *   Esc           → close
 *   ↑ / ↓         → navigate results
 *   Enter         → activate selected
 *
 * The palette is mounted globally from ``AppShell`` so the shortcut
 * works from any page. Results fetch is paused (``enabled: open``) so
 * we don't poll any of the underlying APIs when the dialog is closed.
 */
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  ArrowRight,
  BadgeCheck,
  Bell,
  BookOpen,
  Boxes,
  CornerDownLeft,
  Database,
  Gauge,
  Inbox,
  RadioTower,
  Key,
  Layers,
  LayoutDashboard,
  Megaphone,
  MessageSquare,
  Network,
  PlayCircle,
  Printer,
  ScrollText,
  Search,
  Send,
  SlidersHorizontal,
  TerminalSquare,
  Users,
  Workflow as WorkflowIcon,
  type LucideIcon,
} from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { canonicalApi } from "@/lib/api-canonical";
import { ingressApi } from "@/lib/api-ingress";
import { workflowsApi } from "@/lib/api-workflows";
import { cn } from "@/lib/cn";
import { usePermissions } from "@/lib/permissions";


// ---------------------------------------------------------------------------
// Item model
// ---------------------------------------------------------------------------

interface CommandItem {
  id: string;
  /** Group header this item appears under. */
  group: "nav" | "workflow" | "ingress" | "canonical";
  title: string;
  subtitle?: string;
  icon: LucideIcon;
  onActivate: () => void;
  /** Lower-case haystack used for fuzzy match. */
  haystack: string;
}


// ---------------------------------------------------------------------------
// Static nav items (mirror the sidebar)
// ---------------------------------------------------------------------------

interface NavSpec {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  /** Optional extra search terms (e.g. it.json synonyms). */
  alias?: string;
  /** RBAC resource gating this page — hidden when the caller holds no
   *  grant on it (mirrors the sidebar's permission-aware filtering). */
  resource?: string;
}

// Mirrors the sidebar — keep icons + order + resources in sync with
// ``components/layout/Sidebar.tsx::NAV_ITEMS``.
const NAV_ITEMS: NavSpec[] = [
  { to: "/dashboard", resource: "dashboard", labelKey: "sidebar.dashboard", icon: LayoutDashboard },
  { to: "/topology", resource: "topology", labelKey: "sidebar.topology", icon: Network, alias: "topologia map bus" },
  // Osserva
  { to: "/activity", resource: "executions", labelKey: "sidebar.activity", icon: Activity, alias: "attività feed eventi notifiche" },
  { to: "/messages", resource: "messages", labelKey: "sidebar.messages", icon: MessageSquare, alias: "messaggi inbound ricevuti" },
  { to: "/workflow-runs", resource: "executions", labelKey: "sidebar.workflowRuns", icon: PlayCircle, alias: "esecuzioni run" },
  { to: "/deliveries", resource: "outbound", labelKey: "sidebar.deliveries", icon: Send, alias: "consegne outbox dispatch http connector dead letter retry" },
  { to: "/aggregate-buckets", resource: "workflows", labelKey: "sidebar.aggregateBuckets", icon: Layers, alias: "bucket" },
  { to: "/audit", resource: "audit", labelKey: "sidebar.auditLog", icon: ScrollText, alias: "audit log" },
  { to: "/settings", resource: "settings", labelKey: "sidebar.settings", icon: SlidersHorizontal, alias: "impostazioni settings odino voce ssrf egress webhook policy sicurezza avanzate" },
  // Costruisci
  { to: "/workflows", resource: "workflows", labelKey: "sidebar.workflows", icon: WorkflowIcon },
  { to: "/canonical", resource: "canonical", labelKey: "sidebar.canonical", icon: Boxes, alias: "modelli schema" },
  { to: "/mappings", resource: "mappings", labelKey: "sidebar.mappings", icon: Database },
  // Connessioni
  { to: "/ingress", resource: "ingress", labelKey: "sidebar.ingress", icon: Inbox, alias: "ingressi webhook sftp" },
  { to: "/gateways", resource: "gateways", labelKey: "sidebar.gateways", icon: RadioTower, alias: "gateway on-prem agent firewall edge connector tunnel" },
  { to: "/rate-limits", resource: "rate_limits", labelKey: "sidebar.rateLimits", icon: Gauge, alias: "throttle qps concurrency ingress egress queue reject" },
  { to: "/credentials", resource: "credentials", labelKey: "sidebar.credentials", icon: Key, alias: "secret vault smtp telegram bot password credenziali" },
  { to: "/service-accounts", resource: "service_accounts", labelKey: "sidebar.serviceAccounts", icon: BadgeCheck, alias: "service account api key integration bot machine identity token servizio integrazione" },
  // Allerte
  { to: "/alerts", resource: "alerts", labelKey: "sidebar.alerts", icon: Bell, alias: "alert regole notifiche" },
  { to: "/channels", resource: "channels", labelKey: "sidebar.channels", icon: Megaphone, alias: "canali email telegram in_app bell notification destination" },
  // AI
  { to: "/ai/executions", resource: "odino", labelKey: "sidebar.executions", icon: Activity, alias: "esecuzioni run log llm token costo turno" },
  { to: "/ai/sandbox", resource: "odino", labelKey: "sidebar.sandbox", icon: TerminalSquare, alias: "sandbox codice esecuzioni python compute workspace spazio lavoro file" },
  // Amministrazione
  { to: "/report-types", resource: "reports", labelKey: "sidebar.reports", icon: Printer, alias: "report tipi stampa pdf designer" },
  { to: "/users", resource: "users", labelKey: "sidebar.users", icon: Users, alias: "utenti gruppi" },
  // Aiuto
  { to: "/docs", labelKey: "sidebar.docs", icon: BookOpen, alias: "documentazione" },
];


// ---------------------------------------------------------------------------
// Hook: global Cmd+K toggle
// ---------------------------------------------------------------------------

export function useCommandPalette() {
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return { open, setOpen };
}


// ---------------------------------------------------------------------------
// Palette component
// ---------------------------------------------------------------------------

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = React.useState("");
  const [highlight, setHighlight] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (open) {
      setQuery("");
      setHighlight(0);
      // Focus the input after the dialog mounts. RAF so the focus
      // lands AFTER the browser paints the input.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Scroll the highlighted row into view when the user navigates with
  // the keyboard. Without this, ↓ past the visible window leaves the
  // selection invisible at the bottom.
  const listRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-cmd-idx="${highlight}"]`,
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [highlight, open]);

  // ── Data sources (only fetched while the palette is open) ──────
  const workflowsQ = useQuery({
    queryKey: ["palette", "workflows"],
    queryFn: () => workflowsApi.list({ page_size: 100 }),
    enabled: open,
    staleTime: 60_000,
  });
  const ingressQ = useQuery({
    queryKey: ["palette", "ingress"],
    queryFn: () => ingressApi.list({ page_size: 100 }),
    enabled: open,
    staleTime: 60_000,
  });
  const canonicalQ = useQuery({
    queryKey: ["palette", "canonical"],
    queryFn: () => canonicalApi.list({ page_size: 100 }),
    enabled: open,
    staleTime: 60_000,
  });

  // Permission-aware: pages the caller can't open are not offered
  // (mirrors the sidebar; fail-open while /me loads).
  const { canSee } = usePermissions();

  // ── Build the flat item list ───────────────────────────────────
  const items = React.useMemo<CommandItem[]>(() => {
    const out: CommandItem[] = [];

    for (const n of NAV_ITEMS.filter((n) => !n.resource || canSee(n.resource))) {
      const label = t(n.labelKey);
      out.push({
        id: `nav:${n.to}`,
        group: "nav",
        title: label,
        subtitle: n.to,
        icon: n.icon,
        onActivate: () => navigate(n.to),
        haystack: `${label} ${n.alias ?? ""} ${n.to}`.toLowerCase(),
      });
    }

    for (const w of workflowsQ.data?.items ?? []) {
      out.push({
        id: `wf:${w.id}`,
        group: "workflow",
        title: w.name,
        subtitle: `${w.direction} · ${w.canonical_model}.v${w.canonical_version}`,
        icon: WorkflowIcon,
        onActivate: () => navigate(`/workflows/${w.id}`),
        haystack: `${w.name} ${w.direction} ${w.canonical_model} ${(w.tags ?? []).join(" ")}`.toLowerCase(),
      });
    }

    for (const i of ingressQ.data?.items ?? []) {
      out.push({
        id: `ing:${i.id}`,
        group: "ingress",
        title: i.name,
        subtitle: i.type,
        icon: Inbox,
        onActivate: () => navigate(`/ingress?focus=${i.id}`),
        haystack: `${i.name} ${i.type} ${(i.tags ?? []).join(" ")}`.toLowerCase(),
      });
    }

    for (const c of canonicalQ.data?.items ?? []) {
      out.push({
        id: `can:${c.canonical_type}.v${c.version}`,
        group: "canonical",
        title: `${c.canonical_type} v${c.version}`,
        subtitle: c.status,
        icon: Boxes,
        onActivate: () => navigate(`/canonical`),
        haystack: `${c.canonical_type} v${c.version} ${c.status} ${(c.tags ?? []).join(" ")}`.toLowerCase(),
      });
    }

    return out;
  }, [t, navigate, canSee, workflowsQ.data, ingressQ.data, canonicalQ.data]);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    // Tokenised AND match: every word in the query must appear
    // somewhere in the haystack. Cheap, predictable, good enough.
    const tokens = q.split(/\s+/);
    return items.filter((it) => tokens.every((tok) => it.haystack.includes(tok)));
  }, [items, query]);

  // Group items in display order. Empty groups are skipped.
  const grouped = React.useMemo(() => {
    const groups: Array<{
      key: CommandItem["group"];
      label: string;
      items: CommandItem[];
    }> = [
      { key: "nav", label: t("command.group.nav", "Navigation"), items: [] },
      { key: "workflow", label: t("command.group.workflow", "Workflows"), items: [] },
      { key: "ingress", label: t("command.group.ingress", "Ingress sources"), items: [] },
      { key: "canonical", label: t("command.group.canonical", "Canonical models"), items: [] },
    ];
    const byKey = new Map(groups.map((g) => [g.key, g] as const));
    for (const it of filtered) {
      byKey.get(it.group)?.items.push(it);
    }
    return groups.filter((g) => g.items.length > 0);
  }, [filtered, t]);

  // Flat ordered list for keyboard navigation — mirrors the visual
  // order of ``grouped`` so ↑/↓ moves smoothly across groups.
  const flat = React.useMemo(
    () => grouped.flatMap((g) => g.items),
    [grouped],
  );

  React.useEffect(() => {
    setHighlight(0);
  }, [query]);

  function activate(item: CommandItem) {
    item.onActivate();
    onOpenChange(false);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, Math.max(flat.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (flat[highlight]) activate(flat[highlight]);
    }
  }

  if (!open) return null;

  // Diagnostic state for the data fetches — surface errors so the
  // user sees WHY workflows / ingress / canonical might be empty.
  const isFetchingAny =
    workflowsQ.isLoading || ingressQ.isLoading || canonicalQ.isLoading;
  const fetchErrors = [
    workflowsQ.error && `workflows: ${(workflowsQ.error as Error).message}`,
    ingressQ.error && `ingress: ${(ingressQ.error as Error).message}`,
    canonicalQ.error && `canonical: ${(canonicalQ.error as Error).message}`,
  ].filter(Boolean) as string[];

  return (
    // Backdrop + centred dialog. Click on the backdrop closes.
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-[12vh] backdrop-blur-sm"
      onClick={() => onOpenChange(false)}
    >
      <div
        role="dialog"
        aria-label={t("command.title", "Command palette")}
        className="w-full max-w-3xl overflow-hidden rounded-xl border border-border bg-elevated shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex h-14 items-center gap-3 border-b border-border px-4">
          <Search className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t(
              "command.placeholder",
              "Search workflows, ingress, canonical, pages…",
            )}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            data-1p-ignore
            data-lpignore="true"
            className="flex-1 bg-transparent text-base placeholder:text-muted-foreground focus:outline-none"
          />
          <kbd className="hidden rounded border border-border bg-surface px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground sm:inline">
            esc
          </kbd>
        </div>

        {/* Surface fetch errors at the top instead of silently showing
            nav-only — the user reported "only nav appears" and it
            was impossible to know if a query had failed. */}
        {fetchErrors.length > 0 && (
          <div className="border-b border-border bg-danger/10 px-4 py-2 text-[11px] text-danger">
            {fetchErrors.map((e, i) => (
              <div key={i} className="font-mono">{e}</div>
            ))}
          </div>
        )}

        <div ref={listRef} className="max-h-[60vh] overflow-y-auto py-1">
          {flat.length === 0 ? (
            <div className="px-4 py-10 text-center text-xs text-muted-foreground">
              {isFetchingAny
                ? t("command.loading", "Loading…")
                : t("command.empty", "No results.")}
            </div>
          ) : (
            grouped.map((g) => (
              <div key={g.key} className="mb-1">
                <div className="flex items-center justify-between px-4 pb-1 pt-2">
                  <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">
                    {g.label}
                  </span>
                  <span className="font-mono text-[10px] text-muted-foreground/50">
                    {g.items.length}
                  </span>
                </div>
                <ul>
                  {g.items.map((it) => {
                    const globalIdx = flat.indexOf(it);
                    const isActive = globalIdx === highlight;
                    const Icon = it.icon;
                    return (
                      <li
                        key={it.id}
                        role="option"
                        aria-selected={isActive}
                        data-cmd-idx={globalIdx}
                        onMouseEnter={() => setHighlight(globalIdx)}
                        onClick={() => activate(it)}
                        className={cn(
                          "flex cursor-pointer items-center gap-3 px-4 py-2",
                          "hover:bg-muted/70",
                          isActive && "bg-accent/15",
                        )}
                      >
                        <Icon
                          className="h-4 w-4 shrink-0 text-muted-foreground"
                          strokeWidth={1.5}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm">{it.title}</div>
                          {it.subtitle && (
                            <div className="truncate font-mono text-[11px] text-muted-foreground">
                              {it.subtitle}
                            </div>
                          )}
                        </div>
                        {isActive && (
                          <CornerDownLeft
                            className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                            strokeWidth={1.5}
                          />
                        )}
                        {!isActive && (
                          <ArrowRight
                            className="h-3.5 w-3.5 shrink-0 text-muted-foreground/30"
                            strokeWidth={1.5}
                          />
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>

        <div className="flex items-center justify-between border-t border-border bg-surface px-3 py-1.5 text-[10px] text-muted-foreground">
          <div className="flex items-center gap-2">
            <kbd className="rounded border border-border bg-elevated px-1 py-0.5 font-mono">↑↓</kbd>
            <span>{t("command.hint.navigate", "Navigate")}</span>
            <kbd className="rounded border border-border bg-elevated px-1 py-0.5 font-mono">↵</kbd>
            <span>{t("command.hint.activate", "Go")}</span>
          </div>
          <div>{flat.length} {t("command.hint.results", "results")}</div>
        </div>
      </div>
    </div>
  );
}
